import type { Organization, TenantScope } from '@onefootprint/request-types/dashboard';

import type {
  DASHBOARD_ALLOW_ASSUMED_WRITES,
  DASHBOARD_AUTHORIZATION_HEADER,
  DASHBOARD_IS_LIVE_HEADER,
} from '../../config/constants';

export type UserSession = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  scopes: TenantScope[];
  isAssumedSession: boolean;
  isAssumedSessionEditMode?: boolean;
  isFirmEmployee?: boolean;
  isRiskOpsEmployee?: boolean;
  isCxOpsEmployee?: boolean;
  /** Whether the Agents tab/API is accessible. Source of truth lives on the backend. */
  isAgentsEnabled?: boolean;
  /**
   * Whether the user's role is a restricted Auditor role. Drives the auditor dashboard view
   * (banner + tab allowlist). Source of truth lives on the backend.
   */
  isAuditor?: boolean;
  /** For Auditor roles, the entity tags the user is scoped to. Empty for all other roles. */
  auditorEntityTags?: string[];
};

export type OrgSession = {
  isLive: boolean;
} & Pick<
  Organization,
  | 'isProdNeuroEnabled'
  | 'isProdSentilinkEnabled'
  | 'isSandboxRestricted'
  | 'logoUrl'
  | 'id'
  | 'name'
  | 'allowedPreviewApis'
  | 'featureGates'
>;

export type MetaSession = {
  createdNewTenant: boolean;
  isFirstLogin: boolean;
  requiresOnboarding: boolean;
};

export type Session = {
  auth?: string;
  user?: UserSession;
  org?: OrgSession;
  meta: MetaSession;
};

export const defaultSession = {
  auth: undefined,
  user: undefined,
  org: undefined,
  meta: {
    createdNewTenant: false,
    isFirstLogin: false,
    requiresOnboarding: false,
  },
};

// Whenever changing this, make sure to read this guide:
// https://www.notion.so/onefootprint/Migrating-session-w-Zustand-92cc5a563d6747ca80fd689232c5b7b4
export type UserSessionState = {
  data: Session;
  update: (data: Partial<Session>) => void;
  reset: () => void;
};

export type AuthHeaders = {
  [DASHBOARD_AUTHORIZATION_HEADER]: string;
  [DASHBOARD_IS_LIVE_HEADER]: string;
  [DASHBOARD_ALLOW_ASSUMED_WRITES]?: string;
};
